import React, { useState, useEffect } from 'react';
import {
	Input,
	Checkbox,
	Button,
	Space,
	Table,
	Popconfirm,
	Tooltip,
	Modal,
	Tag,
	Form,
	Select,
	Spin,
	message,
} from 'antd';
import { DeleteOutlined, SearchOutlined, ExclamationCircleOutlined, EditOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { connect } from 'react-redux';

import InputRules from '../../../data/InputRules';
import Util from '../../../util/Util';
import { Actions } from '../../../store/actions';
import VehicleEditForm from './VehicleEditForm';
import ReactImage from '../../../components/Image';

const layout = {
	labelCol: {
		span: 3,
	},
	wrapperCol: {
		span: 8,
	},
};
const tailLayout = {
	wrapperCol: {
		offset: 3,
		span: 16,
	},
};

const VehicleSearch = ({ searchedAds, users, fetchUsers, searchAds, deleteVehicleAd, updateFeatureStatus }) => {
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [editModal, setEditModal] = useState(false);
	const [editVehicle, setEditVehicle] = useState();
	const [fetchingVehicles, setFetchingVehicles] = useState(false);
	const [type, setType] = useState('');
	const [userType, setUserType] = useState('');
	const [query, setQuery] = useState('');
	const [user, setUser] = useState('');
	const [featured, setFeatured] = useState(0);

	useEffect(() => {
		fetchUsers();
	}, []);

	const updateAdStatus = async (id, status) => {
		await updateFeatureStatus(id, { featured: status });
		setFetchingVehicles(true);
		setTimeout(async () => {
			await searchAds(type, query, user, featured);
		}, 1000);
		setFetchingVehicles(false);
	};

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: function filterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			);
		},
		filterIcon: function filter(filtered) {
			return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;
		},
		onFilter: (value, record) =>
			record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
		render: function render(text) {
			return searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			);
		},
	});

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const onDelete = async (id) => {
		await deleteVehicleAd(id);
		setFetchingVehicles(true);
		setTimeout(async () => {
			await searchAds(type, query, user, featured);
		}, 1000);
		setFetchingVehicles(false);
	};

	const onFinish = async (values) => {
		setFetchingVehicles(true);
		await searchAds(values.type, values.query, values.user, featured);
		setTimeout(() => {
			setFetchingVehicles(false);
		}, 1000);
	};

	const onFinishFailed = (errorInfo) => {
		message.error('Something went wrong!');
	};

	const columns = [
		{
			title: 'Image',
			dataIndex: 'image',
			key: 'image',
			render: function image(text, record) {
				return (
					<Space size="middle">
						<ReactImage src={record.image} width={50} />
					</Space>
				);
			},
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => {
				if (a._id.toLowerCase() < b._id.toLowerCase()) return -1;
				if (a._id.toLowerCase() > b._id.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('name'),
		},
		{
			title: 'Interior Color',
			dataIndex: 'interiorColor',
			key: 'interiorColor',
			sorter: (a, b) => {
				if (a._id.toLowerCase() < b._id.toLowerCase()) return -1;
				if (a._id.toLowerCase() > b._id.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('color'),
		},
		{
			title: 'Exterior Color',
			dataIndex: 'exteriorColor',
			key: 'exteriorColor',
			sorter: (a, b) => {
				if (a._id.toLowerCase() < b._id.toLowerCase()) return -1;
				if (a._id.toLowerCase() > b._id.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('color'),
		},
		{
			title: 'Price',
			dataIndex: 'price',
			key: 'price',
			sorter: (a, b) => a.price - b.price,
			...getColumnSearchProps('price'),
		},
		{
			title: 'Year',
			dataIndex: 'year',
			key: 'year',
			sorter: (a, b) => a.year - b.year,
			...getColumnSearchProps('year'),
		},
		{
			title: 'Posted By',
			dataIndex: 'user',
			key: 'user',
			sorter: (a, b) => {
				if (a.user.name.toLowerCase() < b.user.name.toLowerCase()) return -1;
				if (a.user.name.toLowerCase() > b.user.name.toLowerCase()) return 1;
				return 0;
			},
			render: function user(text, record) {
				return <Space size="middle">{`${record?.user?._id?.fullName}`}</Space>;
			},
		},
		{
			title: 'Posted on',
			key: 'createdAt',
			dataIndex: 'createdAt',
			sorter: (a, b) => {
				if (a.createdAt.toLowerCase() < b.createdAt.toLowerCase()) return -1;
				if (a.createdAt.toLowerCase() > b.createdAt.toLowerCase()) return 1;
				return 0;
			},
			render: function user(text, record) {
				return <Space size="middle">{Util.timeSince(record.createdAt)}</Space>;
			},
		},
		{
			title: 'Feature',
			key: 'featured',
			render: function feature(text, record) {
				return (
					<Space size="middle">
						{record.featured?.status ? (
							<>
								<Tag color="volcano">Featured</Tag>
								<Button onClick={() => updateAdStatus(record.id, false)} danger>
									Unfeature
								</Button>
							</>
						) : (
							<Button onClick={() => updateAdStatus(record.id, true)} danger>
								Feature Now
							</Button>
						)}
					</Space>
				);
			},
		},
		{
			title: 'Action',
			key: 'action',
			render: function action(text, record) {
				return (
					<Space size="middle">
						<Tooltip title="View More Details">
							<ExclamationCircleOutlined />
						</Tooltip>
						<Tooltip title="Edit Vehicle">
							<EditOutlined
								onClick={() => {
									setEditVehicle(record);
									setEditModal(!editModal);
								}}
							/>
						</Tooltip>
						<Popconfirm
							title="Are you sure to delete this make?"
							onConfirm={() => onDelete(record._id)}
							okText="Yes"
							cancelText="No"
						>
							<DeleteOutlined />
						</Popconfirm>
					</Space>
				);
			},
		},
	];

	return (
		<div>
			<div>
				<Form
					{...layout}
					name="search-form"
					initialValues={{ remember: true }}
					onFinish={onFinish}
					onFinishFailed={onFinishFailed}
				>
					<Form.Item label="Seller Type" name="userType" rules={InputRules.required('Please select type!')}>
						<Select placeholder="Select type" onSelect={(val) => setUserType(val)}>
							<Select.Option value="seller">Seller</Select.Option>
							<Select.Option value="dealer">Dealer</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item
						label={userType === 'seller' ? 'Seller' : 'Dealer'}
						name="user"
						rules={InputRules.required('Please select user!')}
					>
						<Select
							placeholder="Select user"
							onSelect={(val) => setUser(val)}
							optionFilterProp="search"
							allowClear
							showSearch
						>
							{users &&
								users.length &&
								users
									.filter((user) => user.type === userType)
									.map((user) => (
										<Select.Option
											key={user._id}
											value={user._id}
											search={userType === 'seller' ? user.fullName : user.dealershipName}
										>
											{userType === 'seller' ? user.fullName : user.dealershipName}
										</Select.Option>
									))}
						</Select>
					</Form.Item>
					<Form.Item label="Type" name="type">
						<Select placeholder="Select type" onSelect={(val) => setType(val)}>
							<Select.Option value="used">Used</Select.Option>
							<Select.Option value="new">New</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item label="Search Value" name="query">
						<Input placeholder="Enter search query" onChange={(e) => setQuery(e.target.value)} />
					</Form.Item>
					<Form.Item label="Featured" name="featured">
						<Checkbox onChange={(e) => (e.target.checked ? setFeatured(1) : setFeatured(0))} />
					</Form.Item>
					<Form.Item {...tailLayout}>
						<Button type="primary" htmlType="submit">
							Search
						</Button>
					</Form.Item>
				</Form>
			</div>
			{fetchingVehicles ? (
				<Spin />
			) : (
				<Table
					dataSource={searchedAds}
					columns={columns}
					rowKey={(record) => record._id}
					size="small"
					scroll={{ x: true }}
					pagination={true}
				/>
			)}

			<Modal
				title={`Edit ${editVehicle?.name}`}
				centered={true}
				visible={editModal}
				footer={null}
				onCancel={() => setEditModal(false)}
				destroyOnClose={true}
			>
				<VehicleEditForm
					vehicle={editVehicle}
					setEditModal={() => setEditModal(false)}
					setRefresh={() => setRefresh(!refresh)}
				/>
			</Modal>
		</div>
	);
};

const mapStateToProps = (store) => ({
	searchedAds: store.vehicleAd.searchedAds,
	users: store.user.users,
});

const mapDispatchToProps = (dispatch) => ({
	fetchUsers: () => dispatch(Actions.fetchUsers()),
	searchAds: (type, query, user, featured) => dispatch(Actions.searchAds(type, query, user, featured)),
	deleteVehicleAd: (id) => dispatch(Actions.deleteVehicleAd(id)),
	updateFeatureStatus: (id, body) => dispatch(Actions.updateFeatureStatus(id, body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VehicleSearch);
